import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';

const accountColors = {
  TFSA: { bg: '#E8F5E9', border: '#2E7D32', text: '#2E7D32' },
  RRSP: { bg: '#E3F2FD', border: '#1565C0', text: '#1565C0' },
  FHSA: { bg: '#FFF8E1', border: '#F9A825', text: '#E65100' },
};

const PriorityCard = ({ rank, account, amount, taxSaving, reason, isFirst, marginalRate }) => {
  const { t } = useTranslation();
  const color = accountColors[account] || accountColors.TFSA;

  return (
    <Box
      sx={{
        border: `2px solid ${isFirst ? color.border : '#EEF0F3'}`,
        borderRadius: 3,
        p: 3,
        position: 'relative',
        bgcolor: isFirst ? `${color.bg}40` : '#fff',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.06)' },
      }}
    >
      {/* Rank badge */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box
          sx={{
            width: 32, height: 32, borderRadius: '50%',
            bgcolor: isFirst ? color.border : '#EEF0F3',
            color: isFirst ? '#fff' : '#5F6B7A',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 14,
          }}
        >
          {rank}
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 20, color: color.text }}>
          {account}
        </Typography>
        {isFirst && (
          <Chip
            label={t('calc.results.recommended')}
            size="small"
            sx={{
              bgcolor: color.border, color: '#fff',
              fontWeight: 700, fontSize: 12, height: 24, ml: 'auto',
            }}
          />
        )}
      </Box>

      {/* Amount + tax saving */}
      <Box sx={{ display: 'flex', gap: 3, mb: 1.5, flexWrap: 'wrap' }}>
        {amount > 0 && (
          <Box>
            <Typography sx={{ fontSize: 12, color: '#5F6B7A' }}>
              {t('calc.results.yearLimit', { year: '2026' })}
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1A1A1A', fontVariantNumeric: 'tabular-nums' }}>
              ${amount.toLocaleString()}
            </Typography>
          </Box>
        )}
        <Box>
          <Typography sx={{ fontSize: 12, color: '#5F6B7A' }}>
            {t('calc.results.taxSaving')}
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: account === 'TFSA' ? '#2E7D32' : '#1A1A1A', fontVariantNumeric: 'tabular-nums' }}>
            {taxSaving > 0 ? `~$${Math.round(taxSaving).toLocaleString()}` : t('calc.results.taxFreeGrowth')}
          </Typography>
          {taxSaving > 0 && amount > 0 && marginalRate && (
            <Typography sx={{ fontSize: 12, color: '#5F6B7A' }}>
              {t('calc.results.taxSavingDetail', {
                amount: amount.toLocaleString(),
                rate: `${(marginalRate * 100).toFixed(1)}%`,
              })}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Reason */}
      <Typography sx={{ fontSize: 14, color: '#5F6B7A', lineHeight: 1.6 }}>
        {reason}
      </Typography>
    </Box>
  );
};

const PriorityCards = ({ priority, marginalRate }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {priority.map((item, idx) => (
        <PriorityCard
          key={item.account}
          rank={idx + 1}
          account={item.account}
          amount={item.amount || 0}
          taxSaving={item.taxSaving || 0}
          reason={item.reason}
          isFirst={idx === 0}
          marginalRate={marginalRate}
        />
      ))}
    </Box>
  );
};

export default PriorityCards;
